import React, { useRef, useState } from "react";
import { Button } from "components/Button";
import { DataGridColumnConfig, DataGridFilterType } from "components/DataGrid/types";
import { Form, Offcanvas, OverlayTrigger, Stack, Tooltip } from "react-bootstrap";
import { DataGridFilterInput } from "./DataGridFilterInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataGridSortModalInputs } from "./DataGridSortModal/DataGridSortModalInputs";
import { isEqual, isNil } from "lodash";
import { useFilter } from "./useFilter";

export const DataGridFilterChips: React.FC<{
    className?: string;
    filter: DataGridFilterType;
    columns: DataGridColumnConfig[];
    isFilterEmpty?: boolean;
    getFilterValue: ReturnType<typeof useFilter>["getFilterValue"];
    formTitle: string;
    toggleFilter: () => void;
    showFilter: boolean;
    onFilterClear: () => void;
    onSortClear: () => void;
    onFilterSubmit: (filterValue?: DataGridFilterType) => void;
    onSortChange: (key: string, forceAsc?: boolean) => void;
    sortBy?: string;
    sortAsc?: boolean;
}> = ({
    filter,
    isFilterEmpty,
    getFilterValue,
    sortBy,
    sortAsc,
    columns,
    formTitle,
    toggleFilter,
    showFilter,
    onFilterClear,
    onSortClear,
    onFilterSubmit,
    onSortChange,
}) => {
    const [value, setValue] = useState<DataGridFilterType>(filter);
    const [previousFilter, setPreviousFilter] = useState<DataGridFilterType>(filter);
    const sortInputsRef = useRef<React.ElementRef<typeof DataGridSortModalInputs> | null>(null);

    if (!isEqual(filter, previousFilter)) {
        setValue(filter);
        setPreviousFilter(filter);
    }

    const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const selectedValue = sortInputsRef.current?.selectedSortBy ?? "";
        const selectedSort = sortInputsRef.current?.selectedSortAsc;
        onSortChange(selectedValue, selectedSort);

        onFilterSubmit(value);
        toggleFilter();
    };

    const onChange = (paramName: string, paramValue: string) => {
        setValue({ ...value, [paramName]: paramValue });
    };

    const onClearAll = () => {
        if (!sortInputsRef?.current) return;
        sortInputsRef.current.reset();

        setValue({});
        onFilterClear();
        onSortClear();
    };

    return (
        <Offcanvas style={{ height: "87vh" }} show={showFilter} placement="bottom" onHide={toggleFilter}>
            <Offcanvas.Header closeButton={true}>
                <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip>Use * as a wildcard in your search to find potential matches (e.g., ABC123*).</Tooltip>}
                >
                    <FontAwesomeIcon icon={["fal", "circle-info"]} size="lg" color="grey" />
                </OverlayTrigger>
                <Offcanvas.Title as="h6" className="m-auto">
                    {formTitle}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <hr className="m-0" />
            <Offcanvas.Body>
                <Form className="d-flex flex-column h-100" onSubmit={onSubmitForm}>
                    {!isNil(sortBy) && !isNil(sortAsc) && (
                        <DataGridSortModalInputs
                            className="mb-3"
                            ref={sortInputsRef}
                            columns={columns}
                            sortBy={sortBy}
                            sortAsc={sortAsc}
                            getFilterValue={getFilterValue}
                        />
                    )}

                    <hr style={{ color: "grey" }} />

                    {columns.map((column, index) => (
                        <Form.Group key={index} className="mb-3 data-grid-filter">
                            <Form.Label for={`${column.key}-filter`} className="text-capitalize">
                                {column.name.split("_").join(" ")}
                            </Form.Label>
                            <DataGridFilterInput
                                name={column.key}
                                column={column}
                                value={value[column.key] as any}
                                showPlaceholder={true}
                                onChange={(value) => onChange(column.key, value)}
                            />
                        </Form.Group>
                    ))}

                    <hr style={{ color: "grey" }} className="mb-3" />

                    <Stack className="mt-auto pb-2" direction="horizontal" gap={3}>
                        {!isFilterEmpty && (
                            <Button
                                key="sliders"
                                style={{
                                    color: "initial",
                                    borderColor: "var(--bg-active) !important",
                                }}
                                className="bg-transparent border"
                                onClick={() => onClearAll()}
                            >
                                Clear all
                            </Button>
                        )}
                        <Button variant="primary" type="submit" className="ms-auto">
                            Apply
                        </Button>
                    </Stack>
                </Form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};
